import { useState } from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button, EmptyState, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { minutesSince, money, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi } from './Reports.tsx';

/**
 * Carts left in the online shop: who left them, what was in them, and what the staff did about
 * it (an e-mail, a call, set aside). The shop can also write once on its own, a few hours later.
 */

type Show = 'open' | 'won_back' | 'all';
interface Cart {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  lines: { name: string; qty: number; total: number; available: boolean }[];
  subtotal: number;
  count: number;
  changed_at: string;
  state: 'open' | 'ordered' | 'dismissed';
  reminded_at: string | null;
  won_back: boolean;
  link: string | null;
}
interface Carts {
  carts: Cart[];
  stats: { open: number; open_value: number; reminded: number; won_back: number; won_value: number };
  settings: { remind_carts: boolean; remind_after_hours: number };
  mail: boolean;
}

const HOURS = [1, 2, 3, 6, 12, 24];

function badgeOf(c: Cart): { tone: Tone; icon: IconName; label: string } {
  if (c.won_back) return { tone: 'ok', icon: 'trendUp', label: t('sc.won_back') };
  if (c.state === 'ordered') return { tone: 'neutral', icon: 'checkCircle', label: t('sc.ordered') };
  if (c.state === 'dismissed') return { tone: 'neutral', icon: 'archive', label: t('sc.set_aside') };
  if (c.reminded_at) return { tone: 'info', icon: 'mail', label: minutesSince(c.reminded_at) < 1 ? t('sc.reminded_now') : t('sc.reminded', { when: since(c.reminded_at) }) };
  return { tone: 'warn', icon: 'cart', label: t('sc.waiting') };
}

export function CartsTab() {
  const { can } = useSession();
  const { toast, fail } = useUi();
  const [show, setShow] = useState<Show>('open');
  const { data, reload } = useAction<Carts>('shop.carts', { show });
  const { busy, run } = useSave();
  const [working, setWorking] = useState<string | null>(null);

  const act = async (id: string, name: string, done: string) => {
    setWorking(id + name);
    await run(() => action(name, { cart: id }), done);
    setWorking(null);
    await reload();
  };
  const copy = (c: Cart) => {
    const text = `${window.location.origin}${c.link}`;
    void navigator.clipboard?.writeText(text).then(() => toast(t('sc.link_copied')), fail);
  };
  const saveSettings = (next: Carts['settings']) => void run(() => action('shop.save_cart_settings', next), t('sc.settings_saved')).then(() => reload());

  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  const s = data.stats;
  return (
    <div className="sc">
      <section className="rep-kpis" aria-label={t('sc.at_a_glance')}>
        <Kpi label={t('sc.k_left')} value={String(s.open)} hint={t('sc.k_left_hint', { amount: money(s.open_value) })} warn={s.open > 0} />
        <Kpi label={t('sc.k_reminded')} value={String(s.reminded)} hint={t('sc.k_30_days')} />
        <Kpi label={t('sc.k_won_back')} value={String(s.won_back)} hint={t('sc.k_won_value', { amount: money(s.won_value) })} />
      </section>

      {can('shop.manage') ? (
        <section className="sc-auto card">
          <div className="sc-auto-text">
            <h2>
              <Icon name="mail" size={18} /> {t('sc.auto_title')}
            </h2>
            <p className="muted">{t('sc.auto_body')}</p>
            {!data.mail ? (
              <p className="callout callout-info">
                <Icon name="info" size={16} />
                <span>{t('sc.no_mail')}</span>
                {can('marketing.view') ? (
                  <Link className="btn btn-secondary btn-sm" to="/marketing?tab=settings">
                    {t('sc.set_up_mail')}
                  </Link>
                ) : null}
              </p>
            ) : null}
          </div>
          <div className="sc-auto-controls">
            <Switch
              checked={data.settings.remind_carts}
              disabled={busy || (!data.mail && !data.settings.remind_carts)}
              label={t('sc.auto_switch')}
              onChange={(on) => saveSettings({ ...data.settings, remind_carts: on })}
            />
            <label className="sc-hours">
              <span>{t('sc.after')}</span>
              <select
                className="input"
                value={data.settings.remind_after_hours}
                disabled={busy || !data.settings.remind_carts}
                onChange={(e) => saveSettings({ ...data.settings, remind_after_hours: Number(e.target.value) })}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {tn('sc.hours', h)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>
      ) : null}

      <div className="segmented sc-filter" role="tablist" aria-label={t('sc.show')}>
        {(['open', 'won_back', 'all'] as const).map((k) => (
          <button key={k} role="tab" aria-selected={show === k} className={show === k ? 'active' : ''} onClick={() => setShow(k)}>
            {t(`sc.show_${k}` as MessageKey)}
          </button>
        ))}
      </div>

      {data.carts.length ? (
        <ul className="sc-list">
          {data.carts.map((c) => {
            const b = badgeOf(c);
            const open = c.state === 'open';
            return (
              <li key={c.id} className="sc-cart card">
                <div className="sc-cart-head">
                  <div>
                    <strong className="sc-name">{c.name || t('sc.no_name')}</strong>
                    <span className="sc-contact">
                      {c.phone ? (
                        <a href={`tel:${c.phone.replace(/\s/g, '')}`}>
                          <Icon name="phone" size={14} /> {c.phone}
                        </a>
                      ) : null}
                      {c.email ? (
                        <a href={`mailto:${c.email}`}>
                          <Icon name="mail" size={14} /> {c.email}
                        </a>
                      ) : null}
                    </span>
                  </div>
                  <div className="sc-cart-side">
                    <strong className="tabular">{money(c.subtotal)}</strong>
                    <span className="muted">{minutesSince(c.changed_at) < 1 ? t('sc.left_now') : t('sc.left_ago', { when: since(c.changed_at) })}</span>
                  </div>
                </div>
                <ul className="sc-lines">
                  {c.lines.map((l, i) => (
                    <li key={i} className={l.available ? undefined : 'gone'}>
                      <span>
                        {l.qty} × {l.name}
                      </span>
                      {l.available ? <span className="tabular muted">{money(l.total)}</span> : <span className="muted">{t('sc.not_sold')}</span>}
                    </li>
                  ))}
                </ul>
                <div className="sc-cart-foot">
                  <StatusBadge tone={b.tone} icon={b.icon} label={b.label} />
                  {open && can('shop.handle') ? (
                    <div className="sc-actions">
                      {c.link ? (
                        <Button size="sm" variant="ghost" icon="copy" onClick={() => copy(c)}>
                          {t('sc.copy_link')}
                        </Button>
                      ) : null}
                      {!c.reminded_at ? (
                        <Button size="sm" variant="ghost" icon="phone" busy={working === `${c.id}shop.cart_contacted`} onClick={() => void act(c.id, 'shop.cart_contacted', t('sc.noted'))}>
                          {t('sc.contacted')}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" icon="archive" busy={working === `${c.id}shop.dismiss_cart`} onClick={() => void act(c.id, 'shop.dismiss_cart', t('sc.set_aside_done'))}>
                        {t('sc.set_aside_btn')}
                      </Button>
                      {c.email && data.mail ? (
                        <Button
                          size="sm"
                          variant="primary"
                          icon="send"
                          busy={working === `${c.id}shop.remind_cart`}
                          onClick={() => void act(c.id, 'shop.remind_cart', t('sc.sent', { email: c.email! }))}
                        >
                          {t('sc.remind')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="cart" title={show === 'won_back' ? t('sc.none_won') : t('sc.none')} body={show === 'open' ? t('sc.none_hint') : undefined} />
      )}
    </div>
  );
}
